import { apiMetadataItemBlueprint } from 'library/ts/interfaces/metadata/api/IAPIMetadataItem';
import { AssetEmbedType } from 'library/ts/model/enums/content/AssetEmbedType';
import { MPXStatus } from 'library/ts/model/enums/mpx/MPXStatus';
import { AbstractTagClass } from 'tag/ts/tag/abstract/AbstractTagClass';
import { TagEvent } from 'tag/ts/tag/events/TagEvent';
import { TagEventVO } from 'tag/ts/tag/events/TagEventVO';
import { TagLoggerString } from 'tag/ts/tag/model/enums/TagLoggerString';
import { TagErrorCode } from 'tag/ts/tag/model/error/TagErrorCode';
import { TagOnceSettingsManager } from 'tag/ts/tag/model/TagOnceSettingsManager';
import { ConfigType } from 'tag/ts/tag/model/type/ConfigType';
import { AdUtilities } from 'tag/ts/tag/utilities/AdUtilities';
import { ConfigUtilities } from 'tag/ts/tag/utilities/ConfigUtilities';
import { SiteUtilities } from 'tag/ts/tag/utilities/SiteUtilities';
import { TagContentUtilities } from 'tag/ts/tag/utilities/TagContentUtilities';
export class TagModel extends AbstractTagClass {
    #config = {};
    #contentUtilities;
    #adUtilities;
    #siteUtilities;
    #onceSettingsManager;
    constructor(argument) {
        super({
            ...argument,
            name: 'TagModel'
        });
        this.#adUtilities = new AdUtilities({ tag: argument.tag });
        this.#contentUtilities = new TagContentUtilities({ tag: argument.tag });
        this.#siteUtilities = new SiteUtilities({ tag: argument.tag });
        this.#onceSettingsManager = new TagOnceSettingsManager({ tag: argument.tag });
        this.tagEventsHandler(this.#onTagEvent, [
            TagEvent.ON_API_LOAD_VIDEO_REQUEST
        ]);
    }
    get adUtilities() {
        return this.#adUtilities;
    }
    get contentUtilities() {
        return this.#contentUtilities;
    }
    get siteUtilities() {
        return this.#siteUtilities;
    }
    get onceSettingsManager() {
        return this.#onceSettingsManager;
    }
    get config() {
        return this.#config;
    }
    get apiMetadata() {
        if (this.utils.Type.isArray(this?.params?.CONTENT?.apiMetadata) &&
            this.params.CONTENT.apiMetadata.length > 0) {
            return this.params.CONTENT.apiMetadata[0];
        }
        else {
            return apiMetadataItemBlueprint();
        }
    }
    loadConfigs = (configType, onSuccessCallback, onFailureCallback, defaultConfig = undefined) => {
        this?.logger?.log('loadConfigs:', 'configType:', configType, 'defaultConfig:', defaultConfig);
        const onSuccess = (config) => {
            this?.logger?.log('loadConfigs: Success:', config);
            this.#config = this.utils.DeepMerge.deepmerge(this.#config, config);
            if (this.utils.Type.isFunction(onSuccessCallback)) {
                onSuccessCallback(this.config);
            }
            if (configType === ConfigType.TAG) {
                this.#informApplication(TagEvent.ON_MODEL_READY);
            }
        };
        const onFailure = () => {
            this?.logger?.warn('loadConfigs: Failure:');
            if (configType === ConfigType.TAG) {
                this.#informApplication(TagEvent.ON_MODEL_FAILURE);
            }
        };
        if (this.utils.Type.isObject(this.params.TAG.config) === false) {
            this.params.TAG.config = { tag: [], skin: [] };
        }
        else {
            Object.keys(ConfigType).forEach((key) => {
                if (this.utils.Type.isArray(this.params.TAG.config[ConfigType[key]]) === false) {
                    this.params.TAG.config[ConfigType[key]] = [];
                }
            });
        }
        const processConfig = (config) => {
            const decorateURL = (url) => {
                url = this.utils.URL.addCacheToken(url, this.runtimeVO.debug.cacheBust);
                if (this.runtimeVO.top.ssl) {
                    url = this.utils.URL.toHTTPS(url);
                }
                return url;
            };
            for (let i = 0; i < config.length; i++) {
                const item = config[i];
                if (this.utils.Type.isString(item) && this.utils.URL.valid(item)) {
                    let url = item;
                    url = decorateURL(url);
                    config[i] = url;
                }
            }
            return config;
        };
        const processDefaultTagConfig = (defaultTagConfig) => {
            const projectInfo = window.PlayerServicesProjectInfo;
            const shakaURL = `${this.runtimeVO.url.root}${projectInfo.dependencies.bundle.tag.dir.value}/` +
                `${projectInfo.dependencies.bundle.tag.player.shaka.dir.output.value}/` +
                `${projectInfo.dependencies.bundle.tag.player.shaka.filename.value}`;
            defaultTagConfig.Imports.params.low_level.shaka.url.regular = shakaURL;
            const hlsjsURL = `${this.runtimeVO.url.root}${projectInfo.dependencies.bundle.tag.dir.value}/` +
                `${projectInfo.dependencies.bundle.tag.player.hlsjs.dir.output.value}/` +
                `${projectInfo.dependencies.bundle.tag.player.hlsjs.filename.value}`;
            defaultTagConfig.Imports.params.low_level.hlsjs.url.regular = hlsjsURL;
            return defaultTagConfig;
        };
        switch (configType) {
            case ConfigType.TAG:
                if (this.utils.Type.isDefined(defaultConfig)) {
                    this.#config.tag = processDefaultTagConfig(defaultConfig);
                }
                this.params.TAG.config.tag = processConfig(this.params.TAG.config.tag);
                this?.logger?.log('loadConfigs:', configType, this.params.TAG.config.tag);
                ConfigUtilities.getConfigs(this.uuid, { tag: this.params.TAG.config.tag }, onSuccess, onFailure);
                break;
            case ConfigType.SKIN:
                if (this.utils.Type.isDefined(defaultConfig)) {
                    this.#config.skin = defaultConfig;
                }
                this.params.TAG.config.skin = processConfig(this.params.TAG.config.skin);
                this?.logger?.log('loadConfigs:', configType, this.params.TAG.config.skin);
                ConfigUtilities.getConfigs(this.uuid, { skin: this.params.TAG.config.skin }, onSuccess, onFailure);
                break;
        }
    };
    #informApplication = (eventType) => {
        const eventData = this.getTagEventDataVO();
        eventData.paramsVO = this.params;
        const tagEventVO = this.getTagEventVO(eventType, this.tag, eventData);
        this.dispatchTagEvent(tagEventVO);
        if (eventType === TagEvent.ON_METADATA_READY) {
            this.dispatchTagEvent(new TagEventVO(TagEvent.ON_MODEL_READY, this.tag, undefined, this, this.name));
        }
    };
    #checkMetadata = () => {
        this?.logger?.log('checkMetadata', this.params.CONTENT.apiMetadata);
        const assetEmbedType = this.contentUtilities.assetEmbedType(this.params);
        if (assetEmbedType === AssetEmbedType.ASSET_URL) {
            this.#informApplication(TagEvent.ON_METADATA_READY);
            return;
        }
        if (assetEmbedType === AssetEmbedType.PID) {
        }
        if (assetEmbedType === AssetEmbedType.CID) {
        }
        const processAPIMetadata = (metadataArray) => {
            if (this.utils.Type.isArray(metadataArray) && this.utils.Type.isEmpty(metadataArray) === false) {
                metadataArray.forEach((apiMetadataItem) => {
                    if (this.utils.Type.isEmpty(apiMetadataItem) === false) {
                        Object.keys(apiMetadataItemBlueprint()).forEach((key) => {
                            switch (key) {
                                case 'availableForProfileTypes':
                                case 'downloadCountrySet':
                                case 'premiumFeatures':
                                case 'videoProperties':
                                    if (apiMetadataItem[key] === undefined) {
                                        apiMetadataItem[key] = [];
                                    }
                                    break;
                            }
                        });
                        apiMetadataItem.videoTitle = apiMetadataItem.title;
                    }
                });
            }
            return metadataArray;
        };
        const done = (apiMetadata = this.params.CONTENT.apiMetadata) => {
            switch (apiMetadata[0].status === MPXStatus.AVAILABLE) {
                case true:
                    apiMetadata = processAPIMetadata(apiMetadata);
                    this.#informApplication(TagEvent.ON_METADATA_READY);
                    break;
                case false:
                    this.dispatchTagEvent(this.getTagEventVO(TagEvent.ON_FATAL_ERROR, this.tag, {
                        fatalError: {
                            code: TagErrorCode.METADATA_SERVICE_NOT_ALLOWED,
                            msg: {
                                original: undefined,
                                custom: this.tag.errorManager.createErrorMessageByCode(TagErrorCode.METADATA_SERVICE_NOT_ALLOWED)
                            },
                            error: { isServiceAllowed: false }
                        }
                    }));
                    break;
            }
        };
        const apiMetadataValid = this.utils.Type.isArray(this?.params?.CONTENT?.apiMetadata)
            &&
                this.params.CONTENT.apiMetadata.length > 0
            &&
                !this.utils.Type.isEmpty(this.params.CONTENT.apiMetadata[0]);
        switch (apiMetadataValid) {
            case true:
                done();
                break;
            case false:
                this?.logger?.warn('checkMetadata:', 'WARNING: apiMetadata is not set in params! Stop execution.');
                break;
        }
    };
    #checkParams = (paramsVO = {}) => {
        this?.logger?.log('checkParams:', paramsVO);
        if (this.utils.Type.isEmpty(paramsVO))
            paramsVO = {};
        const isValid = (params) => {
            const output = { valid: true, msg: undefined };
            switch (this.contentUtilities.assetEmbedType(params)) {
                case undefined:
                    output.valid = false;
                    output.msg = 'ERROR: multiple assets (pid, assetURL, contentId) are set in params or not set at all. Make sure only one is set while others are undefined! Stop execution!';
                    break;
                case AssetEmbedType.CID:
                    output.valid = false;
                    output.msg = 'ERROR: Content Id is not playable in Smart Tag! Stop execution!';
                    break;
            }
            return output;
        };
        switch (this.utils.Type.isObject(paramsVO)) {
            case true:
                this.params = this.utils.DeepMerge.deepmerge(this.params, paramsVO);
                this.params = this.tag.tagUtilities.namespaceConverter(this.params);
                this?.logger?.log('checkParams:', 'params post-merge:', this.params);
                const isParamsValid = isValid(this.params);
                switch (isParamsValid.valid) {
                    case true:
                        this.#checkMetadata();
                        break;
                    case false:
                        this?.logger?.warn('checkParams:', isParamsValid.msg);
                        break;
                }
                break;
            case false:
                this?.logger?.warn('checkParams:', 'ERROR: params is not an object!');
                break;
        }
    };
    #onTagEvent = (event) => {
        switch (event?.type) {
            case TagEvent.ON_API_LOAD_VIDEO_REQUEST:
                this?.logger?.log(TagLoggerString.ON_TAG_EVENT, event?.type, event);
                this.#checkParams(event?.data?.paramsVO);
                break;
        }
    };
}
